<template>
  <div>
    <header class="ap-header">
      <div class="logo-wrap">
        <div class="logo">AuditionEdge</div>
        <div class="logo-sub">Prep Tool</div>
      </div>
      <div class="profile-badge">
        Will · Male · 21 · plays
        <input type="number" v-model.number="playsMin" class="age-input" min="10" max="99" @change="saveProfile" />–<input type="number" v-model.number="playsMax" class="age-input" min="10" max="99" @change="saveProfile" />
      </div>
    </header>

    <div class="container">
      <div class="api-bar">
        <label>API Key</label>
        <input type="password" v-model="apiKey" placeholder="sk-ant-..." autocomplete="off" />
        <span class="key-status" :class="keyStatusClass">{{ keyStatusText }}</span>
      </div>

      <div class="section">
        <div class="section-label">Paste Full Breakdown</div>
        <textarea v-model="breakdown"
          placeholder="Paste the entire breakdown — all roles, fine print, everything. The tool will find what fits Will and ignore the rest."></textarea>
      </div>

      <div class="section row">
        <div style="flex:1; min-width:140px;">
          <div class="section-label">Medium</div>
          <select v-model="medium">
            <option value="film/TV">Film / TV</option>
            <option value="vertical short form">Vertical Short Form</option>
            <option value="theater">Theater</option>
            <option value="commercial">Commercial</option>
            <option value="voice-over">Voice-Over</option>
            <option value="short film">Short Film</option>
          </select>
        </div>
        <div style="flex:1; min-width:140px;">
          <div class="section-label">Audition Type</div>
          <select v-model="audType">
            <option value="self-tape">Self-Tape</option>
            <option value="in-person first read">In-Person First Read</option>
            <option value="callback">Callback</option>
            <option value="producer session">Producer Session</option>
            <option value="cold read">Cold Read</option>
            <option value="chemistry read">Chemistry Read</option>
          </select>
        </div>
      </div>

      <div class="section">
        <div class="section-label">What do you need?</div>
        <div class="chips">
          <div v-for="chip in chips" :key="chip.val"
            class="chip" :class="{ active: chip.active }"
            @click="chip.active = !chip.active">
            {{ chip.label }}
          </div>
        </div>
      </div>

      <button class="generate-btn" :disabled="loading" @click="generate">
        <span v-if="loading" class="spinner"></span>{{ buttonText }}
      </button>
      <div class="error-msg" :class="{ visible: errorMsg }">{{ errorMsg }}</div>

      <div class="output-area" :class="{ visible: showOutput }">
        <div v-if="roles.length" class="roles-found">
          <div class="roles-found-label">Roles you qualify for</div>
          <div>
            <span v-for="role in roles" :key="role" class="role-tag">{{ role }}</span>
          </div>
        </div>
        <div v-if="noRoles" class="no-roles visible">
          No roles matched your profile in this breakdown. The project may not have anything for you this round.
        </div>
        <div v-if="prepHtml">
          <div class="output-header">
            <div class="output-title">Prep Sheet</div>
            <button class="copy-btn" :class="{ copied: copyLabel !== 'Copy' }" @click="copyOutput">{{ copyLabel }}</button>
          </div>
          <div class="output-card" ref="outputCard" v-html="prepHtml"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const DEFAULTS = { name: 'Will', gender: 'Male', actualAge: 21, playsMin: 18, playsMax: 26 };

export default {
  name: 'AuditionPrep',
  data() {
    const saved = JSON.parse(localStorage.getItem('ap-profile') || '{}');
    return {
      apiKey: '',
      playsMin: saved.playsMin || DEFAULTS.playsMin,
      playsMax: saved.playsMax || DEFAULTS.playsMax,
      breakdown: '',
      medium: 'film/TV',
      audType: 'self-tape',
      chips: [
        { label: 'Character Analysis', val: 'character analysis', active: true },
        { label: 'What Casting Wants', val: 'what casting wants', active: true },
        { label: 'Physical Choices', val: 'physical choices', active: true },
        { label: 'Emotional Through-Line', val: 'emotional through-line', active: false },
        { label: 'Research Hooks', val: 'research hooks', active: false },
        { label: 'Self-Tape Tips', val: 'self-tape tips', active: false },
        { label: 'Sides Breakdown', val: 'sides breakdown', active: false },
        { label: 'Wardrobe Direction', val: 'wardrobe direction', active: false },
        { label: 'Questions to Ask', val: 'questions to ask', active: false },
      ],
      loading: false,
      buttonText: 'Find My Roles & Generate Prep',
      errorMsg: '',
      showOutput: false,
      roles: [],
      noRoles: false,
      prepHtml: '',
      copyLabel: 'Copy',
    }
  },
  computed: {
    keyStatusClass() {
      const v = this.apiKey.trim();
      if (v.startsWith('sk-ant-') && v.length > 20) return 'set';
      if (v.length > 0) return 'empty';
      return 'empty';
    },
    keyStatusText() {
      const v = this.apiKey.trim();
      if (v.startsWith('sk-ant-') && v.length > 20) return 'ready';
      if (v.length > 0) return 'check key';
      return 'not set';
    },
  },
  methods: {
    saveProfile() {
      localStorage.setItem('ap-profile', JSON.stringify({
        playsMin: this.playsMin,
        playsMax: this.playsMax,
      }));
    },
    renderMarkdown(text) {
      return text
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/^---$/gm, '<hr>')
        .replace(/^[-•] (.+)$/gm, '<li>$1</li>')
        .replace(/(<li>[\s\S]+?<\/li>)/g, '<ul>$1</ul>')
        .replace(/\n{2,}/g, '</p><p>')
        .replace(/\n/g, '<br>');
    },
    async callClaude(system, user, maxTokens) {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey.trim(),
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: maxTokens,
          system,
          messages: [{ role: 'user', content: user }],
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e?.error?.message || `API error ${res.status}`);
      }
      const data = await res.json();
      return data.content?.find(b => b.type === 'text')?.text || '';
    },
    async generate() {
      const key = this.apiKey.trim();
      const selected = this.chips.filter(c => c.active).map(c => c.val);

      this.errorMsg = '';
      this.showOutput = false;
      this.noRoles = false;
      this.roles = [];
      this.prepHtml = '';

      if (!key || !key.startsWith('sk-ant-')) { this.errorMsg = 'Enter your Anthropic API key above.'; return; }
      if (!this.breakdown.trim()) { this.errorMsg = 'Paste a breakdown first.'; return; }
      if (!selected.length) { this.errorMsg = 'Select at least one prep type.'; return; }

      this.loading = true;
      this.buttonText = 'Scanning roles...';

      try {
        const filterText = await this.callClaude(
          `You are a casting assistant. Analyze a breakdown and return a single raw JSON object — no markdown, no explanation, no code fences. The object must have exactly three keys: "roles" (array of role name strings that match the actor), "medium" (string, one of: "film/TV", "vertical short form", "theater", "commercial", "voice-over", "short film" — pick closest match), "auditionType" (string, one of: "self-tape", "in-person first read", "callback", "producer session", "cold read", "chemistry read" — infer from context; default to "self-tape" if unclear).`,
          `Actor: ${DEFAULTS.name}, ${DEFAULTS.gender}, age ${DEFAULTS.actualAge}, plays ${this.playsMin}–${this.playsMax}.
Matching rules: include Male or Any gender roles. Include roles where age range overlaps ${this.playsMin}–${this.playsMax}. "18TPY" = 18+ playable range. Include roles with no age specified. Exclude Female-only roles.

BREAKDOWN:
${this.breakdown}

Return only the JSON object.`,
          500
        );

        let roles = [], detectedMedium = null, detectedAudType = null;
        try {
          const parsed = JSON.parse(filterText.replace(/```json|```/g, '').trim());
          roles = parsed.roles || [];
          detectedMedium = parsed.medium || null;
          detectedAudType = parsed.auditionType || null;
        } catch { roles = []; }

        if (detectedMedium) this.medium = detectedMedium;
        if (detectedAudType) this.audType = detectedAudType;

        this.showOutput = true;

        if (!roles.length) {
          this.noRoles = true;
          return;
        }

        this.roles = roles;
        this.buttonText = 'Writing prep sheet...';

        const finalMedium = detectedMedium || this.medium;
        const finalAudType = detectedAudType || this.audType;

        const prepText = await this.callClaude(
          `You are an elite acting coach. Write sharp, specific, actionable audition prep. No fluff. Use ## for role name headers. Each role gets distinct, specific advice.`,
          `Actor: ${DEFAULTS.name}, Male, ${DEFAULTS.actualAge}, plays ${this.playsMin}–${this.playsMax}
Medium: ${finalMedium} | Audition: ${finalAudType}
Qualifying roles: ${roles.join(', ')}
Cover for each role: ${selected.join(', ')}

Full breakdown for context:
${this.breakdown}

Make each role section punchy and immediately usable.`,
          1800
        );

        this.prepHtml = this.renderMarkdown(prepText);
        this.$nextTick(() => {
          this.$refs.outputCard?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      } catch (e) {
        this.errorMsg = e.message || 'Something went wrong. Check your API key and try again.';
      } finally {
        this.loading = false;
        this.buttonText = 'Find My Roles & Generate Prep';
      }
    },
    copyOutput() {
      const text = this.$refs.outputCard?.innerText || '';
      navigator.clipboard.writeText(text).then(() => {
        this.copyLabel = 'Copied!';
        setTimeout(() => { this.copyLabel = 'Copy'; }, 2000);
      });
    },
  },
}
</script>

<style scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
}

.ap-header {
  border-bottom: 1px solid var(--border);
  padding: 1.1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--surface);
}

.logo-wrap {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
}

.logo {
  font-family: 'Playfair Display', serif;
  font-size: 1.4rem;
  color: var(--gold);
}

.logo-sub {
  font-size: 0.7rem;
  color: var(--muted);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.profile-badge {
  font-size: 0.75rem;
  color: var(--gold);
  background: var(--gold-dim);
  border: 1px solid rgba(201, 168, 76, 0.25);
  border-radius: 20px;
  padding: 0.3rem 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.age-input {
  width: 2.5rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(201, 168, 76, 0.4);
  color: var(--gold);
  font-family: 'DM Sans', sans-serif;
  font-size: 0.75rem;
  text-align: center;
  outline: none;
  padding: 0 0.1rem;
  -moz-appearance: textfield;
  appearance: textfield;
}

.age-input::-webkit-inner-spin-button,
.age-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.age-input:focus {
  border-bottom-color: var(--gold);
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
}

.api-bar {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.7rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.75rem;
}

.api-bar label {
  font-size: 0.7rem;
  color: var(--muted);
  white-space: nowrap;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.api-bar input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--text);
  font-size: 0.85rem;
}

.api-bar input::placeholder {
  color: #444;
}

.key-status {
  font-size: 0.7rem;
  padding: 3px 8px;
  border-radius: 20px;
  white-space: nowrap;
}

.key-status.empty {
  background: rgba(224, 90, 90, 0.12);
  color: var(--red);
}

.key-status.set {
  background: rgba(90, 184, 138, 0.12);
  color: var(--green);
}

.section {
  margin-bottom: 1.5rem;
}

.section-label {
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 0.6rem;
}

textarea {
  width: 100%;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text);
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  line-height: 1.6;
  padding: 0.9rem 1rem;
  resize: vertical;
  min-height: 150px;
  outline: none;
  transition: border-color 0.2s;
}

textarea:focus {
  border-color: var(--border-active);
}

textarea::placeholder {
  color: #3a3a3a;
}

.row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

select {
  flex: 1;
  min-width: 140px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text);
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  padding: 0.65rem 2.25rem 0.65rem 0.9rem;
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.9rem center;
  transition: border-color 0.2s;
}

select:focus {
  border-color: var(--border-active);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.chip {
  padding: 0.38rem 0.85rem;
  border-radius: 20px;
  border: 1px solid var(--border);
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
  color: var(--muted);
  background: var(--surface);
}

.chip:hover {
  border-color: var(--border-active);
  color: var(--text);
}

.chip.active {
  border-color: var(--gold);
  background: var(--gold-dim);
  color: var(--gold);
}

.generate-btn {
  width: 100%;
  padding: 0.9rem;
  background: var(--gold);
  color: #0d0d0f;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.95rem;
  font-weight: 500;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
  margin-top: 0.25rem;
}

.generate-btn:hover {
  opacity: 0.88;
}

.generate-btn:active {
  transform: scale(0.99);
}

.generate-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.error-msg {
  background: rgba(224, 90, 90, 0.1);
  border: 1px solid rgba(224, 90, 90, 0.22);
  border-radius: var(--radius);
  padding: 0.75rem 1rem;
  color: var(--red);
  font-size: 0.85rem;
  margin-top: 1rem;
  display: none;
}

.error-msg.visible {
  display: block;
}

.output-area {
  margin-top: 2rem;
  display: none;
}

.output-area.visible {
  display: block;
}

.roles-found {
  background: var(--blue-dim);
  border: 1px solid rgba(90, 155, 224, 0.2);
  border-radius: var(--radius);
  padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
}

.roles-found-label {
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--blue);
  margin-bottom: 0.5rem;
}

.role-tag {
  display: inline-block;
  background: rgba(90, 155, 224, 0.15);
  border: 1px solid rgba(90, 155, 224, 0.25);
  color: var(--blue);
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  margin: 0.2rem 0.25rem 0.2rem 0;
}

.no-roles {
  background: rgba(224, 90, 90, 0.08);
  border: 1px solid rgba(224, 90, 90, 0.18);
  border-radius: var(--radius);
  padding: 1rem 1.25rem;
  color: var(--red);
  font-size: 0.875rem;
  margin-bottom: 1.25rem;
}

.output-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.9rem;
}

.output-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.05rem;
  color: var(--gold);
  font-style: italic;
}

.copy-btn {
  padding: 0.3rem 0.85rem;
  font-size: 0.75rem;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 20px;
  color: var(--muted);
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  transition: all 0.15s;
}

.copy-btn:hover {
  border-color: var(--border-active);
  color: var(--text);
}

.copy-btn.copied {
  border-color: var(--green);
  color: var(--green);
}

.output-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  font-size: 0.9rem;
  line-height: 1.75;
  color: var(--text);
}

.output-card :deep(h1) {
  font-family: 'Playfair Display', serif;
  font-size: 1.2rem;
  color: var(--gold);
  margin: 1.5rem 0 0.5rem;
  font-weight: 600;
}

.output-card :deep(h1:first-child) {
  margin-top: 0;
}

.output-card :deep(h2) {
  font-family: 'Playfair Display', serif;
  font-size: 1rem;
  color: var(--gold);
  margin: 1.1rem 0 0.4rem;
  font-weight: 400;
  font-style: italic;
}

.output-card :deep(h3) {
  font-size: 0.8rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--muted);
  margin: 1rem 0 0.35rem;
}

.output-card :deep(p) {
  margin-bottom: 0.65rem;
}

.output-card :deep(ul),
.output-card :deep(ol) {
  padding-left: 1.25rem;
  margin-bottom: 0.75rem;
}

.output-card :deep(li) {
  margin-bottom: 0.3rem;
}

.output-card :deep(strong) {
  color: #e8e4de;
  font-weight: 500;
}

.output-card :deep(hr) {
  border: none;
  border-top: 1px solid var(--border);
  margin: 1.5rem 0;
}

.spinner {
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 2px solid rgba(13, 13, 15, 0.3);
  border-top-color: #0d0d0f;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 6px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 520px) {
  .ap-header {
    padding: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .container {
    padding: 1.25rem 1rem;
  }

  .row {
    flex-direction: column;
  }
}
</style>
